<template>
  <div class="layer-rundown">
    <draggable
      v-model="posts"
      ref="tgbDropArea"
      class="layer-draggable draggable tgbPostDrop tgbTagboardDrop tgbDropArea scroll-x"
      role="listbox"
      tabindex="0"
      revert-on-spill="true"
      :value="value"
      :move="checkMove"
      @start="onStart"
      @unchoose="onUnchoose"
      v-on="$listeners"
      v-bind="{
        group: { name: 'socialGraphic', put: allowedSourceGroups },
        animation: 200,
        touchStartThreshold: 10,
      }"
    >
      <tagboard
        v-if="containsTagboard"
        class="tagboard"
        :tagboard-id="tagboardId"
        @remove-tagboard="$emit('delete-tagboard')"
      />

      <template v-else>
        <post
          v-for="post in posts"
          :key="`${post.network}:${post.post_id}`"
          :root-item="rootItem"
          :item="post"
          :queued-post="queuedPost"
          :layout="layout"
          @select="$emit('post-clicked', post.post_id)"
          @delete="$emit('delete-post', post.post_id)"
          @play-video="playVideo"
          @toggle-video="togglePlaying"
          @play-preview-video="playVideo(true)"
          @toggle-preview-video="togglePlaying(true)"
        />
      </template>

      <div
        v-if="!posts.length || isDragOver"
        slot="header"
        class="tgbPostDropHelp tgbDropAreaHelp"
      >
        <p>Drag and drop a tagboard or posts here</p>
      </div>
    </draggable>
  </div>
</template>

<script>
import Draggable from 'vuedraggable';
import { mapActions } from 'vuex';

import PostsListComponent from '../../routes/posts/PostsList.vue';
import PostService from '../../../services/post-loader';

import Tagboard from './TimelineSocialTagboard.vue';
import Post from '../post/TimelinePost.vue';

const PostUtils = require('@tagboard/helpers/post-utils');
const MediaProxy = require('@tagboard/helpers/media-proxy');

export default {
  name: 'SocialPostDropzone',

  components: {
    Draggable,
    Post,
    PostsListComponent,
    Tagboard,
  },

  props: {
    // List of social posts currently attached to graphic
    value: {
      type: Array,
      required: true,
    },

    // Currently selected post ID. Will be shown when graphic is live.
    queuedPost: {
      type: String,
      default: '',
    },

    // Parent timeline item
    layout: {
      type: Object,
      required: true,
    },

    // Root timeline item (for graphic groups)
    rootItem: {
      type: Object,
      default: () => null,
    },
  },

  data() {
    return {
      allowedSourceGroups: ['tagboard', 'posts', 'socialGraphic'],
      isDragOver: false,
    };
  },

  computed: {
    posts: {
      get() {
        return this.value ?? [];
      },

      set(v) {
        this.$emit('value', v);
      },
    },

    tagboardId() {
      if (!this.containsTagboard) {
        return null;
      }

      const [post] = this.posts;
      return post._id || post.id;
    },

    containsTagboard() {
      return this.posts?.length && this.posts[0].type === 'tagboard';
    },

    postLookupTable() {
      return this.$store.state.posts.lookupTable;
    },

    production() {
      return this.$store.state.production.production;
    },

    productionId() {
      return this.production._id;
    },

    owner() {
      return this.production.owner;
    },
  },

  methods: {
    ...mapActions([
      'addToPostsLookupTable',
      'updateTimeline',
    ]),

    playVideo(preview) {
      if (preview) {
        this.$store.dispatch('playPreviewVideo');
      } else {
        this.$store.dispatch('playPostVideo');
      }
    },

    togglePlaying(preview) {
      if (preview) {
        this.$store.dispatch('togglePreviewVideo');
      } else {
        this.$store.dispatch('toggleLiveVideo');
      }
    },

    onStart() {
      const dropzones = document.querySelectorAll('.tgbTagboardDrop, .tgbPostDrop');
      for (let i = 0; i < dropzones.length; i += 1) {
        dropzones[i].classList.add('tgbDropArea--active');
      }
    },

    checkMove(e) {
      if (this.$refs.tgbDropArea.$el === e.to.closest('.tgbDropArea')) {
        // skip the other checks if we are reordering
        return true;
      }

      const { type, post_id: postId } = e.draggedContext.element;
      const hasContent = Boolean(e.relatedContext.list.length);
      const isInList = e.relatedContext.list.some((i) => i.post_id === postId);
      const hasTagboard = e.relatedContext.list.some((i) => i.type === 'tagboard');
      const tagboardInvolved = (hasContent && type === 'tagboard') || hasTagboard;
      const notAllowed = tagboardInvolved || isInList;
      const errorMessages = {
        tagboard: 'A full tagboard cannot be presented with other content.',
        duplicate: 'Duplicate posts not allowed.',
      };

      if (notAllowed) {
        e.to.classList.add('tgbDropArea--no');
        e.dragged.style.cursor = 'not-allowed';
        this.error = errorMessages[tagboardInvolved ? 'tagboard' : 'duplicate'];
        return false;
      }

      this.error = false;
      return true;
    },

    onUnchoose(e) {
      e.item.style.cursor = ('pointer');

      if (this.error) {
        this.errorToast(this.error);
      }

      document.querySelectorAll('.tgbDropArea--no').forEach((el) => {
        el.classList.remove('tgbDropArea--no');
        this.error = false;
      });

      document.querySelectorAll('.tgbDropArea--active').forEach((el) => {
        el.classList.remove('tgbDropArea--active');
      });
    },

    postText(postId) {
      const post = this.postLookupTable[postId];
      return post?.text || 'no text';
    },

    isVideo(postId) {
      const post = this.postLookupTable[postId];
      return post?.post_type === 'video' && (post.videos && post.videos.length);
    },

    photoUrl(postId) {
      const post = this.postLookupTable[postId];

      if (!post) {
        return '';
      }

      if (post.photos?.length) {
        let url = post.photos[0].m;

        if (post.network === 'facebook') {
          url = post.photos[0].l || post.photos[0].m;
        } else if (post.network === 'instagram') {
          url = PostUtils.instagramMediaUrl(post, 'm');
        }

        return MediaProxy.genUrl(url) || url;
      }

      if (post.videos?.length) {
        let url = post.videos[0].p;

        if (post.network === 'youtube') {
          url = post.videos[0].t;
        } else if (post.network === 'instagram') {
          url = PostUtils.instagramMediaUrl(post, 'm');
        }

        return MediaProxy.genUrl(url) || url;
      }

      return '';
    },

    networkIconClass(postId) {
      const post = this.postLookupTable[postId];
      if (!post.network) {
        return '';
      }

      if (post.network === 'instagram-stories') {
        return 'icon icon-instagram';
      }

      if (post.network === 'twitter') {
        return 'icon icon-twitterx';
      }

      return `icon icon-${post.network}`;
    },
  },

  watch: {
    async posts(posts) {
      if (posts.some((post) => post.type === 'tagboard')) {
        return;
      }

      await Promise.all(
        posts.map(async (post) => {
          if (post.post_id in this.postLookupTable) {
            return;
          }

          try {
            const data = await PostService.getManyPosts(this.owner, [{
              post_id: post.post_id,
              network: post.network,
            }]);

            this.addToPostsLookupTable(data.results[0]);
            this.updateTimeline();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error getting social posts:', post.post_id, post.network, error);
          }
        }),
      );
    },
  },
};
</script>
